const toDayKey = (iso) => {
    const d = new Date(iso);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const fmtDayLabel = (key) => {
    const [y, m, d] = key.split("-").map((n) => parseInt(n, 10));
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
};

// Build a days array from just the most recent N snapshots — no zero-fill calendar gaps, no
// `start`/`end`. The X-axis becomes "snapshots over time" rather than "days on the calendar",
// which is the right unit when the consumer doesn't care about a specific date window (e.g.
// the dashboard "show me the recent trend" sparklines). One entry per snapshot, oldest → newest.
export const buildDaysFromLastN = (snapshots, n) => {
    if (!Array.isArray(snapshots) || snapshots.length === 0 || !(n > 0)) return [];
    const sorted = snapshots.slice().sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const tail = sorted.slice(-n);
    return tail.map((snap) => {
        const key = toDayKey(snap.timestamp);
        return { key, label: fmtDayLabel(key), snapshot: snap };
    });
};

export const buildDays = (start, end, snapshots) => {
    if (!start || !end) return [];

    const s = new Date(start);
    const e = new Date(end);
    const startUTC = Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate());
    const endUTC = Date.UTC(e.getUTCFullYear(), e.getUTCMonth(), e.getUTCDate());
    if (!Number.isFinite(startUTC) || !Number.isFinite(endUTC) || startUTC > endUTC) return [];

    const index = Object.create(null);
    for (const snap of Array.isArray(snapshots) ? snapshots : []) {
        const k = toDayKey(snap.timestamp);
        const prev = index[k];
        if (!prev || new Date(snap.timestamp) > new Date(prev.timestamp)) index[k] = snap;
    }

    const out = [];
    for (let t = startUTC; t <= endUTC; t += 24 * 3600 * 1000) {
        const key = toDayKey(new Date(t).toISOString());
        out.push({ key, label: fmtDayLabel(key), snapshot: index[key] || null });
    }
    return out;
};

export const sumRecommendations = (r) =>
    (r.implementedTitleCount ?? 0) +
    (r.implementedMetaDescriptionsCount ?? 0) +
    (r.implementedH1sCount ?? 0) +
    (r.implementedDestinationUriCount ?? 0);

export const sumRecommended = (r) =>
    (r.recommendedTitleCount ?? 0) +
    (r.recommendedMetaDescriptionsCount ?? 0) +
    (r.recommendedH1sCount ?? 0) +
    (r.recommendedDestinationUriCount ?? 0);

export const nv = (n) => (n == null ? "—" : n.toLocaleString());

// Count the days in a `useTrendsData().series(...)` array that actually have a snapshot
// (i.e. .value is a number, not null). Used to refuse drawing a single-point "line".
export const realPointCount = (series) =>
    (series || []).reduce((n, d) => n + (typeof d?.value === "number" ? 1 : 0), 0);

// Sum several day-aligned series (from `useTrendsData().series`) into one. A day stays null —
// a gap — only when *every* input is null, so a combined/derived series doesn't re-introduce the
// phantom zeros on non-snapshot days that the raw series is careful to avoid.
export const combineSeries = (...inputs) => {
    const base = inputs[0] || [];
    return base.map((d, i) => {
        const vals = inputs.map((s) => s[i]?.value);
        return {
            dayKey: d.dayKey,
            value: vals.every((v) => v == null) ? null : vals.reduce((a, v) => a + (Number(v) || 0), 0),
        };
    });
};

// Value at the first day in the range that actually has a snapshot (skipping the zero-filled
// gap days). Returns null if the range has no snapshots. Used to measure change-over-period
// against the live "now" value without a missing day reading as a drop to zero.
export const firstSnapshotValue = (days, picker) => {
    const day = (days || []).find((d) => d.snapshot);
    return day ? Number(picker(day.snapshot.stats || {})) || 0 : null;
};

// Delta between the live current value and the start-of-period snapshot.
export const periodDelta = (live, days, picker) => {
    const first = firstSnapshotValue(days, picker);
    if (first == null || !Number.isFinite(live)) return undefined;
    const diff = live - first;
    return { diff, pct: first !== 0 ? (diff / first) * 100 : undefined };
};

export const numDaysToToday = ({ startIso }) => {
    if (!startIso) return 30;
    const start = new Date(startIso);
    const today = new Date();
    const startUTC = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
    const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
    const diffDays = Math.max(0, Math.floor((todayUTC - startUTC) / (24 * 3600 * 1000)));
    return Math.max(1, diffDays + 1);
};

export const durationStringToDays = (p) =>
    ({ "1m": 30, "3m": 90, "6m": 180, "1y": 365, "max": 365 * 3 }[p] || null);

const MS_PER_DAY = 24 * 3600 * 1000;
const startOfUTCDay = (d) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
const isoUTC = (ms) => new Date(ms).toISOString();

const lastNDaysIso = (n) => {
    const endUTC = startOfUTCDay(new Date());
    const startUTC = endUTC - (n - 1) * MS_PER_DAY;
    return { startIso: isoUTC(startUTC), endIso: isoUTC(endUTC) };
};

export function readDateRangeFromSearch(search) {
    const params = new URLSearchParams(search || "");
    const range = params.get("range");
    const start = params.get("start");
    const end = params.get("end");

    const n = durationStringToDays(range);
    if (n) return lastNDaysIso(n);

    if (start && end) {
        const startIso = new Date(`${start}T00:00:00.000Z`).toISOString();
        const endIso = new Date(`${end}T00:00:00.000Z`).toISOString();
        return { startIso, endIso };
    }

    return lastNDaysIso(durationStringToDays("1m"));
}

export const isoToYMD = (iso) => {
    const d = new Date(iso);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};
